import React, { useEffect } from 'react'

import { TabMenu } from '@amusnet/react-ui'
import { useConfigurationStore, useMenuStore, useNavigationStore, usePlayerInfoStore } from '@stores'
import { useMenuTabs } from './useMenuTabs.tsx'
import { formatMoney } from '@utils/money.ts'
import { MenuSectionId } from 'game-ui-types'
import { useTranslation } from '@amusnet/translations/react'

import {
    GameUIMessageScope,
    GameUIMessageType,
    MenuNavigationBarTappedMessage,
    SetSectionIDMenuMessage,
    ViewportType,
} from 'game-ui-types'
import useGetBetAmount from '@hooks/useGetBetAmount'
import { useSoundStore } from '@stores/soundStore'
import { SOUNDS } from '@constants/sounds'

const Menu: React.FC = () => {
    const { t } = useTranslation()

    const setIsMenuOpen = useNavigationStore((state) => state.setIsMenuOpen)
    const viewport = useConfigurationStore((state) => state.viewport)
    const currency = useConfigurationStore((state) => state.currency)
    const balance = usePlayerInfoStore((state) => state.balance)
    const setSelectedTabIndex = useMenuStore((state) => state.setSelectedTabIndex)
    const currentSelectedTabIndex = useMenuStore((state) => state.currentSelectedTabIndex)
    const shouldHideCents = useConfigurationStore((s) => s.currencyHideCents)
    const shouldOpenDefaultSection = useMenuStore((state) => state.shouldOpenDefaultSection)
    const setShouldOpenDefaultSection = useMenuStore((state) => state.setShouldOpenDefaultSection)
    const targetMenuSectionId = useMenuStore((state) => state.targetMenuSectionId)
    const setTargetMenuSectionId = useMenuStore((state) => state.setTargetMenuSectionId)
    const defaultMenuSectionId = useConfigurationStore((state) => state.defaultMenuSectionId)
    const playEngineSound = useSoundStore((state) => state.playEngineSound)
    const sendTotalsInfo = useConfigurationStore((state) => state.sendTotalsInfo)

    const betAmount = useGetBetAmount()

    const marginBottomMap: Partial<Record<ViewportType, string>> = {
        [ViewportType.MOBILE_LANDSCAPE]: 'mb-[15px]',
        [ViewportType.MOBILE_PORTRAIT]: sendTotalsInfo ? 'mb-[33px]' : 'mb-[19px]',
    }

    const tabs = useMenuTabs()

    let currentIndex = currentSelectedTabIndex

    if (targetMenuSectionId) {
        const targetIndex = tabs.findIndex((section) => section.key === targetMenuSectionId)

        if (targetIndex !== -1) {
            currentIndex = targetIndex
        }

        setTargetMenuSectionId(null)
    } else if (shouldOpenDefaultSection) {
        const defaultIndex = tabs.findIndex((section) => {
            return section.key === defaultMenuSectionId
        })

        if (defaultIndex != -1) {
            currentIndex = defaultIndex
            setSelectedTabIndex(defaultIndex)
        }

        setShouldOpenDefaultSection(false)
    }

    const marginBottom = marginBottomMap[viewport] ?? ''

    const getIndexById = (id: MenuSectionId) => {
        const result = tabs.findIndex((section) => {
            return section.key === id
        })

        return result !== -1 ? result : 0
    }

    const onSelectedIndexChanged = (index: number) => {
        setSelectedTabIndex(index)

        const doubleChanceClickMessage: MenuNavigationBarTappedMessage = {
            scope: GameUIMessageScope.MENU,
            type: GameUIMessageType.MENU_NAVIGATION_BAR_TAPPED,
            payload: { id: tabs[index].key },
        }

        window.postMessage(doubleChanceClickMessage)
    }

    useEffect(() => {
        const handleMessage = (event: MessageEvent) => {
            const rawData = event.data
            const type = rawData.type

            if (rawData.scope !== GameUIMessageScope.MENU) {
                return
            }

            switch (type) {
                case GameUIMessageType.SET_MENU_SECTION_ID: {
                    const data = rawData as SetSectionIDMenuMessage

                    setSelectedTabIndex(getIndexById(data.payload.id))
                    break
                }
            }
        }

        window.addEventListener('message', handleMessage, false)

        return () => {
            window.removeEventListener('message', handleMessage, false)
        }
    })

    return (
        <TabMenu
            onCloseClick={() => {
                setIsMenuOpen(false)
                playEngineSound(SOUNDS.HIDE_DENOMINATIONS)
            }}
            balance={formatMoney(balance, shouldHideCents)}
            iconContainerClassName={marginBottom}
            betAmount={formatMoney(betAmount, shouldHideCents)}
            currency={currency}
            layout={viewport}
            tabs={tabs}
            currentIndex={currentIndex}
            onSelectedIndexChanged={onSelectedIndexChanged}
            betLabel={t('common.bet')}
            balanceLabel={t('common.balance')}
        />
    )
}

export default Menu
